import client from './client';

export async function fetchBacklinks(params) {
  const response = await client.get('/api/backlinks', { params });
  return response.data;
}

export async function fetchBacklink(id) {
  const response = await client.get(`/api/backlinks/${id}`);
  return response.data;
}

export async function createBacklink(data) {
  const response = await client.post('/api/backlinks', data);
  return response.data;
}

export async function updateBacklink(id, data) {
  const response = await client.put(`/api/backlinks/${id}`, data);
  return response.data;
}

export async function deleteBacklink(id) {
  const response = await client.delete(`/api/backlinks/${id}`);
  return response.data;
}

export async function checkBacklink(id) {
  const response = await client.post(`/api/backlinks/${id}/check`);
  return response.data;
}

// Manually set a backlink's status. Used when the crawler reports a false
// positive (timeout/removed) but the user has verified by hand the link is live.
export async function markBacklinkStatus(id, status = 'live', notes = null) {
  const response = await client.post(`/api/backlinks/${id}/mark-status`, { status, notes });
  return response.data;
}

// Bulk version — flip many backlinks to a known status in one round trip.
// Returns { updated, skipped_unauthorized, skipped_already_at_status }.
export async function batchMarkBacklinkStatus(backlink_ids, status = 'live', notes = null) {
  const response = await client.post('/api/backlinks/batch-mark-status', {
    backlink_ids,
    status,
    notes,
  });
  return response.data;
}

export async function fetchBacklinkHistory(id) {
  const response = await client.get(`/api/backlinks/${id}/history`);
  return response.data;
}

export async function bulkImportBacklinks(data) {
  const response = await client.post('/api/backlinks/bulk', data);
  return response.data;
}

export async function refreshBacklinkMetrics(id) {
  const response = await client.post(`/api/backlinks/${id}/refresh-metrics`);
  return response.data;
}

// Fetch all backlinks for a project (no pagination limit)
export async function fetchAllBacklinks(project_id, status = null) {
  const params = { project_id, limit: 10000, skip_history: true }; // High limit, skip history enrichment
  if (status) params.status = status;
  const response = await client.get('/api/backlinks', { params });
  return response.data;
}

// Batch operations
export async function batchCheckBacklinks(backlink_ids, project_id) {
  const response = await client.post('/api/backlinks/batch-check', {
    backlink_ids,
    project_id,
  });
  return response.data;
}

export async function batchRefreshMetrics(backlink_ids, project_id) {
  const response = await client.post('/api/backlinks/batch-refresh-metrics', {
    backlink_ids,
    project_id,
  });
  return response.data;
}

// Batch refresh DataForSEO metrics
export async function batchRefreshDFSMetrics(backlink_ids, project_id) {
  const response = await client.post('/api/backlinks/batch-refresh-dfs', {
    backlink_ids,
    project_id,
  });
  return response.data;
}

// ===========================================
// Phase 7: Dashboard Enhancement Endpoints
// ===========================================

// Get backlink stats by status for a project
export async function fetchBacklinkStats(projectId) {
  const response = await client.get(`/api/backlinks/stats/${projectId}`);
  return response.data;
}

// Batch delete backlinks
export async function batchDeleteBacklinks(ids) {
  const response = await client.delete('/api/backlinks/batch', { data: { ids } });
  return response.data;
}

// Batch add tag to backlinks
export async function batchAddTag(ids, tag) {
  const response = await client.patch('/api/backlinks/batch/tag', { ids, tag });
  return response.data;
}

// Batch remove tag from backlinks
export async function batchRemoveTag(ids, tag) {
  const response = await client.delete('/api/backlinks/batch/tag', { data: { ids, tag } });
  return response.data;
}

// ===========================================
// Domain Grouping & Cross-Project Detection
// ===========================================

// Get backlinks grouped by domain for a project
export async function fetchDomainGroups(projectId) {
  const response = await client.get(`/api/backlinks/domain-groups/${projectId}`);
  return response.data;
}

// Check which domains exist in other projects
export async function checkCrossProjectDomains(domains, excludeProjectId) {
  const response = await client.post('/api/backlinks/cross-project-domains', {
    domains,
    exclude_project_id: excludeProjectId,
  });
  return response.data;
}

// Fetch quality distribution for a project
export async function fetchQualityDistribution(projectId) {
  const response = await client.get(`/api/backlinks/quality-distribution/${projectId}`);
  return response.data;
}

// Fetch anchor text analysis for a project
export async function fetchAnchorAnalysis(projectId) {
  const response = await client.get(`/api/backlinks/anchor-analysis/${projectId}`);
  return response.data;
}

// Check domain-level duplicates during import
export async function checkDomainDuplicates(projectId, domains) {
  const response = await client.post('/api/import/check-domain-duplicates', {
    project_id: projectId,
    domains,
  });
  return response.data;
}

// ===========================================
// WebSocket Batch Check Endpoints
// ===========================================

// Start an async batch check (server-side processing via WebSocket)
export async function startAsyncBatchCheck(projectId, mode = 'all', backlinkIds = null) {
  const response = await client.post('/api/batch/check', {
    project_id: projectId,
    mode,
    backlink_ids: backlinkIds,
  });
  return response.data;
}

// Get batch check status for a project
export async function getBatchCheckStatus(projectId) {
  const response = await client.get(`/api/batch/status/${projectId}`);
  return response.data;
}

// Cancel a running batch check
export async function cancelBatchCheck(jobId) {
  const response = await client.delete(`/api/batch/check/${jobId}`);
  return response.data;
}

